import { isHydrating } from '../../../../hooks/custom/use-hydration-status';

type SplitResult = {
  left: string;
  right: string;
  listStart?: number;
};

// Helper function to sanitize HTML by removing empty tags
const sanitizeHTML = (html: string): string => {
  // Skip DOMParser during SSR and initial hydration to prevent mismatches
  if (
    typeof window === 'undefined' ||
    typeof DOMParser === 'undefined' ||
    isHydrating()
  ) {
    return html;
  }

  if (!html || html.trim() === '') {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Remove empty <b></b>, <strong></strong>, <span></span> tags
  const tagsToCheck = ['b', 'strong', 'span'];
  tagsToCheck.forEach((tagName) => {
    const tags = doc.querySelectorAll(tagName);
    tags.forEach((tag) => {
      // Check if tag is empty (no text content and no child elements)
      const hasText = tag.textContent && tag.textContent.trim() !== '';
      const hasChildren = tag.children.length > 0;

      if (!hasText && !hasChildren) {
        const parent = tag.parentNode;
        if (parent) {
          parent.removeChild(tag);
        }
      }
    });
  });

  return doc.body.innerHTML;
};

export const splitBySpecifierHTML = (
  html: string,
  specifier: string,
  occurrenceIndex = 0
): SplitResult => {
  if (!html || !specifier) return { left: html, right: '' };

  // Skip DOMParser during SSR and initial hydration to prevent mismatches
  if (
    typeof window === 'undefined' ||
    typeof DOMParser === 'undefined' ||
    isHydrating()
  ) {
    if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
      console.warn('DOMParser not available (SSR)');
    }
    return { left: html, right: '' };
  }

  // Sanitize HTML before processing
  const sanitizedHTML = sanitizeHTML(html);
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizedHTML, 'text/html');
  const specLower = specifier.toLowerCase();

  // --- Step 1: search for list-based matches (as before) ---
  const topLevelLists = Array.from(doc.querySelectorAll('ol,ul')).filter(
    (list) => !list.closest('li')
  );

  type Match = {
    list?: Element;
    li?: HTMLLIElement;
    listIndex?: number;
    element?: Element;
    globalIndex: number;
  };

  const matches: Match[] = [];
  let globalIndex = 0;

  for (const list of topLevelLists) {
    const items = Array.from(list.children).filter(
      (c): c is HTMLLIElement => c.tagName?.toLowerCase() === 'li'
    );

    for (let i = 0; i < items.length; i++) {
      const li = items[i];
      const text = li.textContent?.toLowerCase() ?? '';
      if (text.includes(specLower)) {
        matches.push({ list, li, listIndex: i, globalIndex });
      }
      globalIndex++;
    }
  }

  // --- Step 2: if no list match, try non-list elements ---
  if (matches.length === 0) {
    const blocks = Array.from(
      doc.body.querySelectorAll('p, div, span, section')
    );
    for (const block of blocks) {
      const text = block.textContent?.toLowerCase() ?? '';
      if (text.includes(specLower)) {
        matches.push({ element: block, globalIndex });
      }
      globalIndex++;
    }
  }

  if (matches.length === 0) {
    console.warn(`Specifier "${specifier}" not found in HTML.`);
    return { left: html, right: '' };
  }

  const chosen = matches[Math.min(occurrenceIndex, matches.length - 1)];
  const serializer = (node: Node) =>
    (node as Element).outerHTML ?? node.textContent ?? '';

  // --- Step 3: handle LIST case ---
  if (chosen.list && chosen.li != null) {
    const parentList = chosen.list;
    const targetLi = chosen.li;
    const items = Array.from(parentList.children).filter(
      (c): c is HTMLLIElement => c.tagName.toLowerCase() === 'li'
    );
    const idx = items.indexOf(targetLi);

    const rootChildren = Array.from(doc.body.childNodes);
    const leftParts: string[] = [];
    const rightParts: string[] = [];
    let foundParentList = false;

    for (const child of rootChildren) {
      if (child === parentList) {
        const tag = parentList.tagName.toLowerCase();

        if (idx > 0) {
          const leftSlice = items
            .slice(0, idx)
            .map((li) => li.outerHTML)
            .join('');
          leftParts.push(`<${tag}>${leftSlice}</${tag}>`);
        }
        const rightSlice = items
          .slice(idx)
          .map((li) => li.outerHTML)
          .join('');
        rightParts.push(`<${tag}>${rightSlice}</${tag}>`);
        foundParentList = true;
      } else if (!foundParentList) {
        leftParts.push(serializer(child));
      } else {
        // Include content after the list in the right side
        rightParts.push(serializer(child));
      }
    }

    const startAttr = parentList.getAttribute('start');
    const parsedStart = startAttr ? parseInt(startAttr, 10) || 1 : 1;
    const listStart =
      parentList.tagName.toLowerCase() === 'ol'
        ? parsedStart + (chosen.listIndex ?? 0)
        : undefined;

    return {
      left: sanitizeHTML(leftParts.join('').trim()),
      right: sanitizeHTML(rightParts.join('').trim()),
      listStart,
    };
  }

  // --- Step 4: handle GENERIC (non-list) block case ---
  if (chosen.element) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild!;
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);

    let matchNode: Text | null = null;
    let matchOffset = 0;
    const searchLower = specifier.toLowerCase();
    let globalCount = 0; // counts occurrences across all text nodes

    while (walker.nextNode()) {
      const node = walker.currentNode as Text;
      const text = node.textContent ?? '';
      let start = 0;

      while (true) {
        const idx = text.toLowerCase().indexOf(searchLower, start);
        if (idx === -1) break;

        if (globalCount === occurrenceIndex) {
          matchNode = node;
          matchOffset = idx;
          break;
        }

        globalCount++; // move to next occurrence
        start = idx + searchLower.length; // move past this occurrence
      }

      if (matchNode) break;
    }

    if (!matchNode) return { left: html, right: '' };

    // Split text node at occurrence
    const afterNode = matchNode.splitText(matchOffset);

    const rangeLeft = doc.createRange();
    rangeLeft.setStart(root, 0);
    rangeLeft.setEndBefore(afterNode);

    const rangeRight = doc.createRange();
    rangeRight.setStartBefore(afterNode);
    rangeRight.setEndAfter(root.lastChild!);

    const tempLeft = doc.createElement('div');
    tempLeft.appendChild(rangeLeft.cloneContents());

    const tempRight = doc.createElement('div');
    tempRight.appendChild(rangeRight.cloneContents());

    return {
      left: sanitizeHTML(tempLeft.innerHTML.trim()),
      right: sanitizeHTML(tempRight.innerHTML.trim()),
    };
  }

  return { left: html, right: '' };
};
